import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../css/navbar.css';
import '../css/HomeLanding.css';

function HomePage() {
    const [longUrl, setLongUrl] = useState('');
    const [customAlias, setCustomAlias] = useState('');
    const [shortUrl, setShortUrl] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [shouldShake, setShouldShake] = useState(false);
    const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';
    const fullShortUrl = shortUrl ? `${window.location.origin}/${shortUrl}` : '';

    const getQrImageUrl = (url) =>
        `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=16&data=${encodeURIComponent(url)}`;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setShortUrl('');

        if (!longUrl.trim()) {
            setShouldShake(true);
            return;
        }

        try {
            const username = localStorage.getItem('username') || 'Anonymous';
            const payload = {
                originalUrl: longUrl,
                creator: username,
            };
            if (customAlias.trim()) {
                payload.customAlias = customAlias.trim();
            }

            const response = await axios.post(
                `${BASE_URL}/links/shorten`,
                payload,
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                }
            );

            if (response.data?.shortUrl) {
                setShortUrl(response.data.shortUrl);
            } else {
                setError('Short URL was not returned from the server.');
            }

            setLongUrl('');
            setCustomAlias('');
        } catch (err) {
            console.error('❌ Failed to shorten URL:', err);
            if (err.response && err.response.data && err.response.data.error) {
                setError(err.response.data.error);
            } else {
                setError('Failed to shorten URL. Please try again.');
            }
        }
    };

    const copyUrl = () => {
        if (shortUrl) {
            navigator.clipboard.writeText(fullShortUrl)
                .then(() => {
                    setIsCopied(true);
                    setTimeout(() => setIsCopied(false), 2000);
                })
                .catch((error) => {
                    console.error('❌ Failed to copy URL to clipboard:', error);
                });
        }
    };

    const openShare = (platform) => {
        if (!shortUrl) return;
        const encoded = encodeURIComponent(fullShortUrl);
        const text = encodeURIComponent('Check this short link:');
        const links = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
            twitter: `https://twitter.com/intent/tweet?url=${encoded}&text=${text}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
            whatsapp: `https://wa.me/?text=${text}%20${encoded}`,
            telegram: `https://t.me/share/url?url=${encoded}&text=${text}`,
            reddit: `https://www.reddit.com/submit?url=${encoded}&title=${text}`,
            pinterest: `https://pinterest.com/pin/create/button/?url=${encoded}&description=${text}`,
            email: `mailto:?subject=BlinkURL%20Link&body=${text}%20${encoded}`,
        };
        if (platform === 'native' && navigator.share) {
            navigator.share({ title: 'BlinkURL', url: fullShortUrl }).catch(() => {});
            return;
        }
        if (links[platform]) {
            window.open(links[platform], '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="home-landing">
            <section className="hero">
                <div className="hero-content">
                    <h1>Shorten Your Long URL</h1>
                    <p>Paste your long URL below to shorten and share it easily!</p>
                    <form className="hero-form" onSubmit={handleSubmit}>
                        <input
                            id="url-input"
                            type="url"
                            placeholder="https://www.yourlongurlgoeshere.com/very/long/link/example"
                            required
                            value={longUrl}
                            onChange={(e) => setLongUrl(e.target.value)}
                            className={shouldShake ? 'shake' : ''}
                            onAnimationEnd={() => setShouldShake(false)}
                        />
                        <button type="submit">Shorten URL</button>
                        <input
                            type="text"
                            placeholder="Custom alias (optional, 4-32 chars)"
                            value={customAlias}
                            onChange={(e) => setCustomAlias(e.target.value)}
                            className="alias-input"
                        />
                    </form>

                    {error && <p className="hero-error">{error}</p>}

                    {shortUrl && (
                        <div className="hero-result">
                            <a
                                href={fullShortUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {fullShortUrl}
                            </a>
                            <button type="button" onClick={copyUrl}>
                                {isCopied ? 'Copied!' : 'Copy'}
                            </button>
                            <div className="share-actions">
                                <button type="button" onClick={() => openShare('native')}>Share</button>
                                <button type="button" onClick={() => openShare('facebook')}>Facebook</button>
                                <button type="button" onClick={() => openShare('twitter')}>X</button>
                                <button type="button" onClick={() => openShare('linkedin')}>LinkedIn</button>
                                <button type="button" onClick={() => openShare('whatsapp')}>WhatsApp</button>
                                <button type="button" onClick={() => openShare('telegram')}>Telegram</button>
                                <button type="button" onClick={() => openShare('reddit')}>Reddit</button>
                                <button type="button" onClick={() => openShare('pinterest')}>Pinterest</button>
                                <button type="button" onClick={() => openShare('email')}>Email</button>
                            </div>
                            <div className="qr-preview">
                                <img src={getQrImageUrl(fullShortUrl)} alt="QR code for shortened URL" />
                                <a
                                    href={getQrImageUrl(fullShortUrl)}
                                    download={`blinkurl-${shortUrl}.png`}
                                >
                                    Download QR
                                </a>
                            </div>
                        </div>
                    )}

                    <div className="hero-cards">
                        <div className="hero-card">
                            <div className="card-icon link"></div>
                            <h3>Easy Sharing</h3>
                            <p>Share your short link anywhere.</p>
                        </div>
                        <div className="hero-card">
                            <div className="card-icon chart"></div>
                            <h3>Track Clicks</h3>
                            <p>Monitor your link's performance.</p>
                        </div>
                        <div className="hero-card">
                            <div className="card-icon shield"></div>
                            <h3>Secure &amp; Reliable</h3>
                            <p>Safe and fast URL shortening.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="why">
                <h2>Why Use BlinkURL?</h2>
                <div className="why-grid">
                    <div className="why-card">
                        <h4>Simplify Your Links</h4>
                        <p>Turn long URLs into short, easy-to-share links.</p>
                    </div>
                    <div className="why-card">
                        <h4>Boost Engagement</h4>
                        <p>Increase clicks and drive more traffic to your content.</p>
                    </div>
                    <div className="why-card">
                        <h4>Safe &amp; Secure</h4>
                        <p>Your links are protected with top-notch security.</p>
                    </div>
                </div>
            </section>

            <section className="learn">
                <div className="learn-inner">
                    <h2>Learn to Grow With BlinkURL</h2>
                    <p>
                        Practical guides for tracking link clicks, posting on Facebook without looking spammy,
                        and measuring campaigns with UTMs.
                    </p>
                    <div className="learn-grid">
                        <Link className="learn-card" to="/blog/track-link-clicks-with-blinkurl-analytics">
                            <strong>Track link clicks and referrers</strong>
                            <span>Understand traffic sources and what drives repeatable growth.</span>
                        </Link>
                        <Link className="learn-card" to="/blog/shorten-links-for-facebook-with-preview-pages">
                            <strong>Shorten links for Facebook safely</strong>
                            <span>Use preview pages to keep the experience transparent and trusted.</span>
                        </Link>
                        <Link className="learn-card" to="/blog/utm-parameters-for-link-tracking">
                            <strong>Use UTMs for clean measurement</strong>
                            <span>Tag campaigns the right way, then shorten the final URL.</span>
                        </Link>
                    </div>
                    <Link className="learn-more" to="/blog">Browse the BlinkURL Blog</Link>
                </div>
            </section>
        </div>
    );
}

export default HomePage;
